import { log } from "@/lib/log";
import { ContentNode as ContentNode } from "./parseHtml";
import { sanitizationSettings } from "./allowlist";

// Turns an html ast into dom nodes. It ignores tags and attributes not on the allowlist.
export function createSafeContentNode(content: ContentNode | ContentNode[]): Node | null {
  if (Array.isArray(content)) {
    const fragment = document.createDocumentFragment();
    for (const item of content) {
      const child = createSafeContentNode(item);
      if (child) {
        fragment.appendChild(child);
      }
    }
    return fragment;
  }

  const isPlainTextNode = typeof content === "string" || typeof content === "number";
  if (isPlainTextNode) {
    return document.createTextNode(String(content));
  }

  const tag = typeof content.tag === "string" ? content.tag : "[undefined]";
  const isAllowedTag = sanitizationSettings.allowedTags.includes(tag);
  if (!isAllowedTag) {
    log("Invalid tag", tag);
    return null;
  }

  const el = document.createElement(tag);

  // Set attributes
  if (content.attrs) {
    for (const [key, value] of Object.entries(content.attrs)) {
      const valueStr = value.toString();

      const isAllowedAttirbute =
        sanitizationSettings.nonBooleanAttributes.includes(key) ||
        value === "" || // boolean attributes
        key.startsWith("data-") ||
        key.startsWith("aria-");
      if (!isAllowedAttirbute) {
        log("Invalid attribute", key);
        continue;
      }

      // Apply allowed schemes to attributes
      if (sanitizationSettings.allowedSchemesAppliedToAttributes.includes(key)) {
        let protocol = "http";
        try {
          protocol = new URL(valueStr).protocol.slice(0, -1);
        } catch (e) {
          log("Invalid URL", valueStr);
        }

        if (!sanitizationSettings.allowedSchemes.includes(protocol)) {
          log("Scheme not allowed", protocol, valueStr);
          continue;
        }
      }

      el.setAttribute(key, valueStr);
    }
  }

  // Append content
  if (content.content) {
    if (Array.isArray(content.content)) {
      for (const item of content.content) {
        const child = createSafeContentNode(item);
        if (child) {
          el.appendChild(child);
        }
      }
    } else {
      const child = createSafeContentNode(content.content);
      if (child) {
        el.appendChild(child);
      }
    }
  }

  return el;
}
